import React, { Fragment } from 'react';

const ACCENT = '#cc9600';

/** Match agency page body stack (see `agencyCopy` on AgencyPage). */
const agencyFont = "font-[Calibri,'Calibri_Light','Segoe_UI',sans-serif]";

const deliverableBodyGrey = 'text-[#363636]';

const DELIVERABLES = [
  {
    key: 'polished',
    title: 'High-end UI as a standard',
    body: "Design is not an afterthought; it's our lead. Every build features intuitive, agency-standard polished UI systems that enhance your reputation.",
  },
  {
    key: 'delivery',
    title: 'Rapid build cycles, from concept to code',
    body: 'Accelerate timelines with modern AI-assisted development workflows. Move from validated prototype to production-ready tooling faster than traditional methods.',
  },
  {
    key: 'workflow',
    title: 'Systems that connect & automate',
    body: 'Beyond portals, we build systems: automate internal tasks, connect data silos (Supabase, API integrations), and ship custom workflow tools that cut repetitive work.',
  },
];

const HOW_STEPS = [
  {
    key: 'brief',
    step: 1,
    title: 'You bring the brief',
    body: 'Share your goals, requirements, and any existing designs or ideas.',
  },
  {
    key: 'build',
    step: 2,
    title: 'I design and build',
    body: 'Your solution ships with clean code, modern UI, and seamless functionality.',
  },
  {
    key: 'deliver',
    step: 3,
    title: 'You deliver with confidence',
    body: 'You launch to your client knowing everything is polished and reliable.',
  },
];

function StepConnector() {
  return (
    <div className="relative hidden w-6 shrink-0 md:block lg:w-8" aria-hidden>
      <div
        className="absolute left-1/2 top-1/2 h-px w-8 -translate-x-1/2 -translate-y-1/2 lg:w-10"
        style={{
          background: `linear-gradient(90deg, transparent 0%, ${ACCENT} 20%, ${ACCENT} 80%, transparent 100%)`,
        }}
      />
    </div>
  );
}

/**
 * @param {{ embedded?: boolean }} props — When true, render inside parent white strip (no outer white frame).
 */
export function AgencyDeliverablesPanel({ embedded = false }) {
  const inner = (
    <>
      <h2
        id="agency-deliverables-heading"
        className="hub-font-title text-xl font-light uppercase tracking-[0.16em] text-neutral-950 sm:text-2xl md:text-3xl"
      >
        Typical deliverables
      </h2>

      {/* Mobile: stacked pairs */}
      <div className="mt-8 space-y-8 md:hidden">
        {DELIVERABLES.map((d) => (
          <div key={d.key} className="border-b border-neutral-200 pb-8 last:border-b-0 last:pb-0">
            <h3 className="text-lg font-semibold leading-snug sm:text-xl" style={{ color: ACCENT }}>
              {d.title}
            </h3>
            <p className={`mt-3 text-sm leading-relaxed sm:text-base ${deliverableBodyGrey}`}>{d.body}</p>
          </div>
        ))}
      </div>

      {/* Desktop: row of titles then row of bodies — tops align per row */}
      <div className="mt-10 hidden md:grid md:grid-cols-3 md:gap-0 md:divide-x md:divide-neutral-200">
        {DELIVERABLES.map((d) => (
          <h3
            key={`${d.key}-title`}
            className="px-6 pb-4 text-lg font-semibold leading-snug lg:px-7 lg:text-xl xl:text-[1.35rem]"
            style={{ color: ACCENT }}
          >
            {d.title}
          </h3>
        ))}
        {DELIVERABLES.map((d) => (
          <p
            key={`${d.key}-body`}
            className={`px-6 pb-2 pt-1 text-base leading-relaxed lg:px-7 ${deliverableBodyGrey}`}
          >
            {d.body}
          </p>
        ))}
      </div>

      {/* Bottom: steps in light boxes + orange connectors */}
      <div
        className="mt-10 border-t border-neutral-200 pt-8 md:mt-12 md:pt-10"
        role="list"
        aria-label="How it works"
      >
        <div className="flex flex-col gap-5 md:flex-row md:items-stretch md:gap-0">
          {HOW_STEPS.map((item, index) => {
            const { key, step, title, body } = item;
            return (
              <Fragment key={key}>
                {index > 0 ? <StepConnector /> : null}
                <div
                  className="min-w-0 flex-1 rounded-sm border border-neutral-200/90 bg-[#f3f4f6] px-4 py-5 shadow-sm sm:px-5 sm:py-6"
                  role="listitem"
                >
                  <p
                    className="text-2xl font-bold tabular-nums leading-none sm:text-3xl"
                    style={{ color: ACCENT }}
                  >
                    {step}
                  </p>
                  <p className="mt-3 text-base font-semibold text-neutral-950 sm:text-lg">{title}</p>
                  <p className={`mt-2 text-sm leading-relaxed sm:text-base ${deliverableBodyGrey}`}>{body}</p>
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    </>
  );

  return (
    <aside className={agencyFont} aria-labelledby="agency-deliverables-heading">
      {embedded ? (
        inner
      ) : (
        <div className="overflow-hidden rounded-none border border-neutral-200 bg-white shadow-lg">
          <div className="p-6 sm:p-8 md:p-10">{inner}</div>
        </div>
      )}
    </aside>
  );
}

export default AgencyDeliverablesPanel;
